// Condition checklist step.

import { TouchButton } from "./MockShell";

export type ConditionState = {
  finishPct: number;
  bore: "clean" | "pitting" | "fouled";
  functionCheck: boolean;
  aftermarket: boolean;
  missingParts: boolean;
  notes: string;
};

type Props = {
  condition: ConditionState;
  setCondition: (c: ConditionState) => void;
  onPrev: () => void;
  onNext: () => void;
};

export function ConditionStep({ condition, setCondition, onNext }: Props) {
  return (
    <>
      <div className="bg-white rounded-2xl p-5">
        <label
          htmlFor="finish"
          className="text-[13px] font-medium text-slate-500 uppercase tracking-wider"
        >
          Finish
        </label>
        <div className="flex items-center gap-4 mt-2">
          <input
            id="finish"
            type="range"
            min="0"
            max="100"
            value={condition.finishPct}
            onChange={(e) => setCondition({ ...condition, finishPct: Number(e.target.value) })}
            className="flex-1 accent-[#052460]"
          />
          <span className="w-14 text-right text-[22px] font-semibold tabular-nums">
            {condition.finishPct}%
          </span>
        </div>
      </div>

      <div className="mt-4 bg-white rounded-2xl p-4">
        <div className="text-[13px] font-medium text-slate-500 uppercase tracking-wider mb-2">
          Bore
        </div>
        <div className="inline-flex gap-1 p-1 rounded-full bg-slate-200/80 w-full">
          {(["clean", "pitting", "fouled"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setCondition({ ...condition, bore: v })}
              className={`flex-1 min-h-10 rounded-full text-[15px] font-medium transition ${
                condition.bore === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 bg-white rounded-2xl overflow-hidden">
        <ToggleRow
          label="Function check passed"
          checked={condition.functionCheck}
          onChange={(v) => setCondition({ ...condition, functionCheck: v })}
        />
        <ToggleRow
          label="Aftermarket modifications"
          checked={condition.aftermarket}
          onChange={(v) => setCondition({ ...condition, aftermarket: v })}
        />
        <ToggleRow
          label="Missing parts or accessories"
          checked={condition.missingParts}
          onChange={(v) => setCondition({ ...condition, missingParts: v })}
        />
      </div>

      <div className="mt-4 bg-white rounded-2xl p-4">
        <label
          htmlFor="notes"
          className="text-[13px] font-medium text-slate-500 uppercase tracking-wider"
        >
          Notes
        </label>
        <textarea
          id="notes"
          value={condition.notes}
          onChange={(e) => setCondition({ ...condition, notes: e.target.value })}
          placeholder="Honest wear notes"
          rows={3}
          className="mt-1.5 w-full rounded-xl bg-slate-100 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#007aff]/30"
        />
      </div>

      <div className="mt-6">
        <TouchButton size="lg" full onClick={onNext}>
          Continue
        </TouchButton>
      </div>
    </>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between px-4 py-3 min-h-12 hairline last:shadow-none cursor-pointer">
      <span className="text-[15px]">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-[31px] w-[51px] shrink-0 rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-[27px] w-[27px] rounded-full bg-white shadow transition transform mt-0.5 ${
            checked ? "translate-x-[22px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </label>
  );
}
